import { v4 as uuidv4 } from 'uuid';
import { SIGNATURE_LIBRARY_STORAGE_KEY } from '../constants/signing';
import type { SavedSignature } from '../types/fields';

const MAX_SIGNATURE_LABEL_LENGTH = 60;

export type UpsertSavedSignatureResult =
  | {
      status: 'created';
      signature: SavedSignature;
      signatures: SavedSignature[];
    }
  | {
      status: 'duplicate';
      signature: SavedSignature;
      signatures: SavedSignature[];
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeLabel(label: string, fallback: string): string {
  const normalized = label.trim().slice(0, MAX_SIGNATURE_LABEL_LENGTH);
  return normalized || fallback;
}

export function normalizeSavedSignature(value: unknown): SavedSignature | null {
  if (!isRecord(value)) {
    return null;
  }

  const { id, dataUrl, label, savedAt, lastUsedAt } = value;
  if (
    typeof id !== 'string' ||
    typeof dataUrl !== 'string' ||
    typeof label !== 'string' ||
    typeof savedAt !== 'string'
  ) {
    return null;
  }

  const normalized: SavedSignature = {
    id,
    dataUrl,
    label: normalizeLabel(label, ''),
    savedAt,
  };

  if (typeof lastUsedAt === 'string') {
    normalized.lastUsedAt = lastUsedAt;
  }

  return normalized;
}

export function loadSavedSignatures(storage: Storage = localStorage): SavedSignature[] {
  try {
    const raw = storage.getItem(SIGNATURE_LIBRARY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.reduce<SavedSignature[]>((signatures, entry) => {
      const normalized = normalizeSavedSignature(entry);
      if (normalized) {
        signatures.push(normalized);
      }
      return signatures;
    }, []);
  } catch {
    return [];
  }
}

export function saveSignaturesToStorage(
  signatures: SavedSignature[],
  storage: Storage = localStorage,
): void {
  storage.setItem(SIGNATURE_LIBRARY_STORAGE_KEY, JSON.stringify(signatures));
}

export function findDuplicateSignature(
  signatures: SavedSignature[],
  dataUrl: string,
): SavedSignature | null {
  return signatures.find((signature) => signature.dataUrl === dataUrl) ?? null;
}

export function upsertSavedSignature(
  signatures: SavedSignature[],
  dataUrl: string,
  label: string,
  now: Date = new Date(),
): UpsertSavedSignatureResult {
  const timestamp = now.toISOString();
  const duplicate = findDuplicateSignature(signatures, dataUrl);

  if (duplicate) {
    const signature = {
      ...duplicate,
      lastUsedAt: timestamp,
    };
    return {
      status: 'duplicate',
      signature,
      signatures: signatures.map((saved) => (saved.id === duplicate.id ? signature : saved)),
    };
  }

  const signature: SavedSignature = {
    id: uuidv4(),
    dataUrl,
    label: normalizeLabel(label, `Signature ${signatures.length + 1}`),
    savedAt: timestamp,
    lastUsedAt: timestamp,
  };

  return {
    status: 'created',
    signature,
    signatures: [...signatures, signature],
  };
}
